import PropTypes from 'prop-types';
import React, {Fragment} from 'react';
import classNames from 'classnames';
import {FormattedMessage} from 'react-intl';
import Draggable from 'react-draggable';

import styles from './card.css';

import shrinkIcon from './icon--shrink.svg';
import expandIcon from './icon--expand.svg';
import bigIcon from './icon--big2.svg';
import smallIcon from './icon--small2.svg';

import rightArrow from './icon--next.svg';
import leftArrow from './icon--prev.svg';

import {
    registerUnityInstance,
    unregisterUnityInstance,
    registerToggleViewCallback,
    unregisterToggleViewCallback
} from '../../lib/unity-bridge.js';

import { Room, RoomEvent, Track, setLogLevel } from 'livekit-client';
import { bootstrapUnitySession, getUserJwt, getSessionInfo } from '../../lib/client-handshake.js';


let globalUnityInstance = null;
let sessionPollInterval = null;

function onUnityReady({ unityInstance }) {
  console.log("onUnityReady (from promise) - Unity instance is loaded.");
  globalUnityInstance = unityInstance;
}
function fireStatusUpdate(status, message = '', countdownTo = null) {
    const detail = { status, message, countdownTo };
    window.dispatchEvent(new CustomEvent('sessionStatusUpdate', { detail }));
    
    // If we're ready, we can stop here.
    if (status === 'ready') {
        console.log("Session is ready, hiding loader.");
    }
}
const ensureAudioPlayback = async (room, videoEl) => {
  try { await room?.startAudio(); } catch {}
  if (videoEl) {
    videoEl.muted = false;
    videoEl.defaultMuted = false;
    try { await videoEl.play(); } catch {}
  }
};

window.unityReady = async function() {
    console.log("window.unityReady (from C#) has been called!");
    
    if (!globalUnityInstance) {
        fireStatusUpdate('error', 'Unity instance failed to load.');
        return;
    }

    const params = new URLSearchParams(window.location.search);
    const devOverrideTime = params.get('devOverrideTime') === 'true';
    const shortCode = params.get('shortCode');

    if (!shortCode) {
        fireStatusUpdate('ready');
        return;
    }

    const checkSession = async () => {
        if (sessionPollInterval) clearInterval(sessionPollInterval);
        sessionPollInterval = null;
        try {
            fireStatusUpdate('loading', 'Connecting to session...');
            const token = await getUserJwt();

            const poll = async () => {
                try {
                    console.log('Polling for session info...');
                    const session = await getSessionInfo(shortCode, devOverrideTime, token);
                    window.dispatchEvent(new CustomEvent('sessionDataReady', { detail: session }));
                    if (session.sessionState === 'before') {
                        clearInterval(sessionPollInterval);
                        sessionPollInterval = null;
                        fireStatusUpdate('countdown', 'Session starts in:', session.startTime);
                    } else if (session.sessionState === 'after') {
                        // clearInterval(sessionPollInterval);
                        // sessionPollInterval = null;
                        // Bootstrap session for replay/review
                        await bootstrapUnitySession({ 
                            unityInstance: globalUnityInstance, 
                            shortCode, 
                            devOverrideTime 
                        });
                        fireStatusUpdate('ready');
                    } else if (session.sessionState === 'during') {
                        if (session.livekit?.serverUrl && session.livekit?.token && !session.livekit?.tokenError) {
                            // We have the keys! Stop polling and start the session.
                            clearInterval(sessionPollInterval);
                            sessionPollInterval = null;
                            await bootstrapUnitySession({ 
                                unityInstance: globalUnityInstance, 
                                shortCode, 
                                devOverrideTime 
                            });
                            fireStatusUpdate('ready');
                        } else {
                            // Session is 'during' but keys are missing. Keep polling.
                            fireStatusUpdate('loading', 'Waiting for video link...');
                        }
                    }
                } catch (err) {
                    clearInterval(sessionPollInterval);
                    sessionPollInterval = null;
                    console.error("Session poll failed:", err);
                    if (err.message.includes("Session info error: 500")) {
                        fireStatusUpdate('error', 'We couldn\'t load the session details. Please try refreshing the page.');
                    } else {
                        fireStatusUpdate('error', 'Failed to get session info. Please try again.');
                    }
                }
            };

            poll(); // Call once immediately
            if (!sessionPollInterval) { // Only start interval if not already cleared
                sessionPollInterval = setInterval(poll, 5000); // Poll every 5s
            }

        } catch (err) { // This catch is now for getUserJwt()
            console.error("Session handshake failed (JWT):", err);
            if (err.message.includes("No Supabase client")) {
                fireStatusUpdate('error', 'It looks like you aren\'t logged in. Please log in to join the session.');
            } else if (err.message.includes("Authentication")) { 
                fireStatusUpdate('error', 'Authentication failed. Please check your login.');
            } else {
                fireStatusUpdate('error', 'Failed to connect to the session. Please try again.');
            }
        }
    };

    window.addEventListener('sessionCountdownFinished', checkSession);
    
    await checkSession();
};

const CardHeader = ({onShrinkExpandCards, totalSteps, step, expanded, isLarge, onTogglePanelSize}) => (
    <div className={expanded ? styles.headerButtons : classNames(styles.headerButtons, styles.headerButtonsHidden)}>
        <div className={styles.headerSpacer} />
        {totalSteps > 1 ? (
            <div className={styles.stepsList}>
                {Array(totalSteps).fill(0)
                    .map((_, i) => (
                        <div
                            className={i === step ? styles.activeStepPip : styles.inactiveStepPip}
                            key={`pip-step-${i}`}
                        />
                    ))}
            </div>
        ) : null}
        <div className={styles.headerButtonsRight}>
            <div
                className={styles.shrinkExpandButton}
                onClick={onTogglePanelSize}
            >
                <img
                    alt=""
                    draggable={false}
                    src={isLarge ? smallIcon : bigIcon}
                />
                {isLarge ? (
                    <FormattedMessage
                        defaultMessage="Small"
                        description="Make panel smaller"
                        id="gui.cards.size.small"
                    />
                ) : (
                    <FormattedMessage
                        defaultMessage="Big"
                        description="Make panel bigger"
                        id="gui.cards.size.big"
                    />
                )}
            </div>
            <div
                className={styles.shrinkExpandButton}
                onClick={onShrinkExpandCards}
            >
                <img
                    alt=""
                    draggable={false}
                    src={expanded ? shrinkIcon : expandIcon}
                />
                {expanded ? (
                    <FormattedMessage
                        defaultMessage="Shrink"
                        description="Title for button to shrink how-to card"
                        id="gui.cards.shrink"
                    />
                ) : (
                    <FormattedMessage
                        defaultMessage="Expand"
                        description="Title for button to expand how-to card"
                        id="gui.cards.expand"
                    />
                )}
            </div>
        </div>
    </div>
);
CardHeader.propTypes = {
    expanded: PropTypes.bool.isRequired, onShrinkExpandCards: PropTypes.func.isRequired, step: PropTypes.number, totalSteps: PropTypes.number
};

const NextPrevButtons = ({isRtl, onNextStep, onPrevStep, expanded}) => (
    <Fragment>
        {onNextStep && <div onClick={onNextStep} className={expanded ? (isRtl ? styles.leftButton : styles.rightButton) : styles.hidden}><img alt="Next" draggable={false} src={isRtl ? leftArrow : rightArrow} /></div>}
        {onPrevStep && <div onClick={onPrevStep} className={expanded ? (isRtl ? styles.rightButton : styles.leftButton) : styles.hidden}><img alt="Previous" draggable={false} src={isRtl ? rightArrow : leftArrow} /></div>}
    </Fragment>
);
NextPrevButtons.propTypes = { expanded: PropTypes.bool.isRequired, isRtl: PropTypes.bool, onNextStep: PropTypes.func, onPrevStep: PropTypes.func };


const UnityStep = ({buildUrl, goName = 'MessageReceiver', onReady}) => {
    const canvasRef = React.useRef(null);
    React.useEffect(() => {
        const script = document.createElement('script');
        script.src = `${buildUrl}/WebGL.loader.js`;
        script.async = true;
        script.onload = () => {
            if (typeof window.createUnityInstance !== 'function' || !canvasRef.current) return;
            const config = {
                dataUrl: `${buildUrl}/WebGL.data.br`,
                frameworkUrl: `${buildUrl}/WebGL.framework.js.br`,
                codeUrl: `${buildUrl}/WebGL.wasm.br`,
                companyName: 'Nexus', // Added to remove warnings
                productName: 'UnityEmbed', // Added to remove warnings
                productVersion: '0.1.0' // Added to remove warnings
            };
            window.createUnityInstance(canvasRef.current, config).then(instance => {
                registerUnityInstance(instance, goName);
                if (typeof onReady === 'function') onReady({unityInstance: instance});
            });
        };
        document.head.appendChild(script);
        return () => {
            if (document.head.contains(script)) {
                document.head.removeChild(script);
            }
            unregisterUnityInstance();
        };
    }, [buildUrl, goName, onReady]);
    return (
        <div style={{width: '100%', height: '100%'}}>
            <canvas ref={canvasRef} id="unity-canvas" style={{width: '100%', aspectRatio: '16/9'}} tabIndex={-1} />
        </div>
    );
};
UnityStep.propTypes = { buildUrl: PropTypes.string.isRequired, goName: PropTypes.string, onReady: PropTypes.func };

class YouTubeStep extends React.Component {
    static getYouTubeId(url) {
        if (!url) return '';
        const match = url.match(/(?:[?&]v=|\/embed\/|\/live\/|youtu\.be\/)([^?&#]+)/);
        return match ? match[1] : '';
    }
    constructor(props) {
        super(props);
        this.player = null;
        this.mountEl = null;
    }
    componentDidMount() {
        if (window.YT && window.YT.Player) this.initPlayer();
        else {
            if (!document.getElementById('yt-iframe-api')) {
                const tag = document.createElement('script');
                tag.id = 'yt-iframe-api';
                tag.src = 'https://www.youtube.com/iframe_api';
                document.head.appendChild(tag);
            }
            const prev = window.onYouTubeIframeAPIReady;
            window.onYouTubeIframeAPIReady = () => {
                if (prev) prev();
                this.initPlayer();
            };
        }
    }
    componentDidUpdate(prevProps) {
        if (!this.player) return;
        if (prevProps.video !== this.props.video) {
            const videoId = YouTubeStep.getYouTubeId(this.props.video);
            if (videoId) this.player.cueVideoById({videoId});
        }
        if (!this.props.expanded) {
            try { this.player.pauseVideo(); } catch (e) { /* ignore */ }
        }
    }
    componentWillUnmount() {
        if (this.player && typeof this.player.destroy === 'function') this.player.destroy();
    }
    initPlayer = () => {
        if (!this.mountEl || !window.YT || !window.YT.Player) return;
        const videoId = YouTubeStep.getYouTubeId(this.props.video);
        if (videoId) {
            this.player = new window.YT.Player(this.mountEl, {
                videoId,
                host: 'https://www.youtube-nocookie.com',
                playerVars: { 
                    rel: 0, 
                    modestbranding: 1, 
                    playsinline: 1,
                    origin: window.location.origin
                },
                events: { onReady: () => !this.props.expanded && this.player.pauseVideo() }
            });
        }
    }
    render() {
        return (
            <div className={classNames(styles.stepVideo, 'drag-cancel')} style={{width: '100%'}}>
                <div style={{width: '100%', aspectRatio: '16/9'}}>
                    <div ref={el => (this.mountEl = el)} style={{width: '100%', height: '100%'}} />
                </div>
            </div>
        );
    }
}
YouTubeStep.propTypes = { video: PropTypes.string.isRequired, expanded: PropTypes.bool.isRequired };
const LiveKitStep = ({ serverUrl, token, expanded }) => {
    const videoRef = React.useRef(null);
    const roomRef = React.useRef(null);
    const [status, setStatus] = React.useState('Initializing...');
    const [hasVideo, setHasVideo] = React.useState(false);
    const [isPlaybackBlocked, setIsPlaybackBlocked] = React.useState(false);

    const startPlayback = React.useCallback(async () => {
        if (!videoRef.current || !roomRef.current) return;
        console.log('[LK] Attempting to start playback...');
        try {
            await roomRef.current.startAudio();
            videoRef.current.muted = false;
            videoRef.current.defaultMuted = false;
            await videoRef.current.play();
            setIsPlaybackBlocked(false); // Success!
            console.log('[LK] Playback started successfully.');
        } catch (e) {
            console.warn('[LK] Playback failed:', e.message);
            if (e.name === 'NotAllowedError') {
                console.warn('[LK] Playback blocked by browser policy.');
                setIsPlaybackBlocked(true);
            }
        }
    }, []);

    React.useEffect(() => {
        setLogLevel('debug');
        const room = new Room({ adaptiveStream: true, dynacast: true });
        roomRef.current = room;
        room.on(RoomEvent.ConnectionStateChanged, s => {
          console.log('[LK] state:', s);
        });
        room.on(RoomEvent.ParticipantConnected, p => {
          console.log('[LK] participant connected:', p.identity);
        });
        room.on(RoomEvent.ParticipantDisconnected, p => {
          console.log('[LK] participant disconnected:', p.identity);
        });
        room.on(RoomEvent.TrackPublished, (pub, participant) => {
          console.log('[LK] track published:', participant.identity, pub.kind, pub.source, 'subscribed=', pub.isSubscribed);
          if (!pub.isSubscribed) {
            pub.subscribe().catch(e => console.warn('[LK] subscribe failed:', e));
          }
        });
        room.on(RoomEvent.TrackSubscribed, (track, pub, participant) => {
          console.log('[LK] track subscribed:', participant.identity, track.kind, pub.source);
         
        if (track.kind === Track.Kind.Video && videoRef.current) {
            track.attach(videoRef.current);
            setHasVideo(true);
        }
        if (track.kind === Track.Kind.Audio) {
            track.attach();
        }
        })

        room.on(RoomEvent.TrackUnsubscribed, (track, pub, participant) => {
            console.log('[LK] track unsubscribed:', participant.identity, track.kind);
            if (track.kind === Track.Kind.Video) {
            track.detach(videoRef.current);
            
            // Check if any *other* video tracks are still subscribed
            let otherVideoSubscribed = false;
            room.participants.forEach(p => {
                p.tracks.forEach(pub => {
                // Check for other subscribed video tracks
                if (pub.track && pub.kind === Track.Kind.Video && pub.trackSid !== track.sid) {
                    otherVideoSubscribed = true;
                }
                });
            });
            setHasVideo(otherVideoSubscribed);
            }
        });

        room
        .connect(serverUrl, token)
        .then(() => {
            setStatus('Connected');
            console.log('[LK] connected. participants:', room.participants?.size);
            room.participants?.forEach((p) => {
            p.tracks?.forEach((pub) => {
              const alreadyHaveTrack = !!pub.track;
              console.log('[LK] existing pub:', p.identity, pub.kind, pub.source, 'track=', alreadyHaveTrack, 'subscribed=', pub.isSubscribed);
              
              if (pub.track?.kind === Track.Kind.Video && videoRef.current) {
                pub.track.attach(videoRef.current);
                // If we find an already-subscribed track, set state here.
                setHasVideo(true); 
              }
              if (pub.track?.kind === Track.Kind.Audio) {
                pub.track.attach();
              }
            })
          });
            // setHasVideo(videoTracksFound);
            startPlayback();
            room.startAudio().catch(() => {})
            if (videoRef.current) {
            videoRef.current.muted = false;
            videoRef.current.defaultMuted = false;
            videoRef.current.play().catch(() => {});
            }
        })
        .catch((e) => setStatus(`Error: ${e.message}`))

        return () => room.disconnect()
    }, [serverUrl, token, startPlayback])

    React.useEffect(() => {
        if (!videoRef.current) return;
        if (expanded) {
            console.log('[LK] expanded, trying to start audio/video');
            startPlayback();
        } else {
            console.log('[LK] collapsed, pausing video');
            videoRef.current.pause();
        }
    }, [expanded, startPlayback])

    React.useEffect(() => {
        const onFirstInteract = () => {
            console.log('[LK] First interaction detected, retrying playback.');
            startPlayback();
        }
        window.addEventListener('pointerdown', onFirstInteract, { once: true })
        return () => window.removeEventListener('pointerdown', onFirstInteract)
    }, [startPlayback])

    const showOverlay = status !== 'Connected' || !hasVideo || isPlaybackBlocked;
    let overlayMessage = status; // 'Initializing...' or 'Error...'
    if (status === 'Connected') {
        if (!hasVideo) {
            overlayMessage = 'Waiting for stream...';
        } else if (isPlaybackBlocked) {
            overlayMessage = '▶ Click to play stream';
        }
    }
    return (
        <div
        style={{
            width: '100%',
            aspectRatio: '16/9',
            background: '#000',
            position: 'relative',
            color: 'white',
            display: 'grid',
            placeItems: 'center',
            cursor: isPlaybackBlocked ? 'pointer' : 'default'
        }}
        >
        <video
            ref={videoRef}
            autoPlay
            playsInline
            style={{ width: '100%', height: '100%', zIndex: 1 }}
        />
        {showOverlay && (
            <div
            style={{ position: 'absolute', zIndex: 2, padding: '1rem' }}
            >
            {overlayMessage}
            </div>
        )}
        </div>
    )
}

LiveKitStep.propTypes = {
    serverUrl: PropTypes.string.isRequired,
    token: PropTypes.string.isRequired,
    expanded: PropTypes.bool.isRequired
};


const UnityYouTubeStep = ({ unity, youtube, livekit, expanded }) => {
    const [isVideoVisible, setVideoVisible] = React.useState(false);

    React.useEffect(() => {
        registerToggleViewCallback(setVideoVisible);
        return () => unregisterToggleViewCallback();
    }, []);

    const unityContainerStyle = { display: isVideoVisible ? 'none' : 'block' };
    const videoContainerStyle = { display: isVideoVisible ? 'block' : 'none' };
    console.log('UnityYouTubeStep rendering with:', {
        hasLiveKit: !!livekit,
        hasYouTube: !!youtube
    });
    const renderVideo = () => {
        if (livekit && livekit.serverUrl && livekit.token) {
            return (
                <LiveKitStep
                    expanded={expanded}
                    serverUrl={livekit.serverUrl}
                    token={livekit.token}
                />
            );
        }
        if (youtube && youtube.videoUrl) {
            return (
                <YouTubeStep
                    expanded={expanded}
                    video={youtube.videoUrl}
                />
            );
        }
        return (
            <div style={{ width: '100%', aspectRatio: '16/9', background: '#222', color: 'white', display: 'grid', placeItems: 'center' }}>
                <p>Video is currently unavailable.</p>
            </div>
        );
    };
    return (
        <div style={{width: '100%'}}>            
            {/* Both containers are always rendered, but one is hidden with CSS */}
            <div style={videoContainerStyle}>
                {renderVideo()}
            </div>
            <div style={unityContainerStyle}>
                <UnityStep
                    buildUrl={unity.buildUrl}
                    goName={unity.goName}
                    onReady={onUnityReady}
                />
            </div>
        </div>
    );
};

UnityYouTubeStep.propTypes = {
    unity: PropTypes.object.isRequired,
    youtube: PropTypes.object,
    livekit: PropTypes.object, // Add livekit as an optional prop
    expanded: PropTypes.bool.isRequired
};

const Cards = props => {
    const { 
        activeDeckId, 
        content, 
        onShrinkExpandCards, 
        onNextStep, 
        onPrevStep, 
        step, 
        expanded,
        dynamicData,
        ...posProps } = props;

    const [isLarge, setIsLarge] = React.useState(false);
    const togglePanelSize = () => setIsLarge(v => !v);
    let {x, y} = posProps;

    if (activeDeckId === null) return null;
    
    const cardHorizontalDragOffset = 400;
    const menuBarHeight = 48;
    if (x === 0 && y === 0) {
        x = 292 + cardHorizontalDragOffset;
        y = window.innerHeight - 320 - 60 - menuBarHeight;
    }

    const steps = content[activeDeckId].steps;
    const currentStep = { ...steps[step] };
    if (dynamicData && currentStep.unityWithYoutube) {
        currentStep.unityWithYoutube = {
            ...currentStep.unityWithYoutube, 
            youtube: (dynamicData.youtube && dynamicData.youtube.watchUrl) ? {
                videoUrl: dynamicData.youtube.watchUrl
            } : null,
            livekit: dynamicData.livekit && dynamicData.livekit.status === 'available' ? {
                serverUrl: dynamicData.livekit.serverUrl,
                token: dynamicData.livekit.token
            } : null
        };
    }
    return (
        <div className={styles.cardContainerOverlay} style={{ width: `${window.innerWidth + (2 * cardHorizontalDragOffset)}px`, height: `${window.innerHeight - menuBarHeight}px`, top: `${menuBarHeight}px`, left: `${-cardHorizontalDragOffset}px` }}>
            <Draggable bounds="parent" cancel=".drag-cancel" position={{x, y}} onDrag={props.onDrag} onStart={props.onStartDrag} onStop={props.onEndDrag}>
                <div className={styles.cardContainer}>
                    <div className={classNames(styles.card, {[styles['card-large']]: isLarge})}>
                        <CardHeader 
                            expanded={expanded} 
                            step={step} 
                            totalSteps={steps.length} 
                            onShrinkExpandCards={onShrinkExpandCards} 
                            isLarge={isLarge} 
                            onTogglePanelSize={togglePanelSize}
                        />
                        <div className={expanded ? styles.stepBody : styles.hidden}>
                            {currentStep.unityWithYoutube ? (
                                <UnityYouTubeStep
                                    expanded={expanded}
                                    unity={currentStep.unityWithYoutube.unity}
                                    youtube={currentStep.unityWithYoutube.youtube}
                                    livekit={currentStep.unityWithYoutube.livekit}
                                />
                            ) : null}
                        </div>
                        <NextPrevButtons expanded={expanded} onNextStep={step < steps.length - 1 ? onNextStep : null} onPrevStep={step > 0 ? onPrevStep : null} isRtl={props.isRtl} />
                    </div>
                </div>
            </Draggable>
        </div>
    );
};

Cards.propTypes = {
    activeDeckId: PropTypes.string,
    content: PropTypes.object.isRequired,
    expanded: PropTypes.bool.isRequired,
    isRtl: PropTypes.bool,
    onNextStep: PropTypes.func.isRequired,
    onPrevStep: PropTypes.func.isRequired,
    onShrinkExpandCards: PropTypes.func.isRequired,
    step: PropTypes.number.isRequired,
    onDrag: PropTypes.func,
    onStartDrag: PropTypes.func,
    onEndDrag: PropTypes.func,
    dynamicData: PropTypes.object,
    x: PropTypes.number,
    y: PropTypes.number 
};

Cards.defaultProps = {
    dynamicData: null
};

export { Cards as default };

